import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { 
  Package, Truck, MapPin, Clock, Phone, CheckCircle2, 
  Store, Home as HomeIcon, RefreshCw, Navigation, X, CreditCard
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';

// Import Leaflet CSS
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons
const createIcon = (color, emoji) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 20px; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">${emoji}</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
};

const storeIcon = createIcon('#22c55e', '🏪');
const customerIcon = createIcon('#0a1628', '🏠');
const driverIcon = createIcon('#f97316', '🚗');

// Component to recenter map when driver moves
const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  return null;
};

// Store location (Ottawa)
const STORE_LOCATION = { lat: 45.3456, lng: -75.7333 };

const LiveOrderTracking = ({ orderId, onBack, user }) => {
  const [order, setOrder] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapCenter, setMapCenter] = useState([45.4215, -75.6972]); // Ottawa default
  const [isPolling, setIsPolling] = useState(true);
  
  const token = localStorage.getItem('auth_token');
  const pollIntervalRef = useRef(null);

  // Fetch order tracking data
  const fetchTracking = async () => {
    try {
      const res = await fetch(`${API_URL}/api/orders/${orderId}/tracking`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error('Failed to fetch tracking info');
      }
      
      const data = await res.json();
      setOrder(data.order);
      setStatusHistory(data.status_history || []);
      
      // Update driver location if available
      if (data.driver_location && data.driver_location.latitude) {
        setDriverLocation({
          lat: data.driver_location.latitude,
          lng: data.driver_location.longitude,
          heading: data.driver_location.heading,
          updated_at: data.driver_location.updated_at
        });
        // Center on driver when out for delivery
        if (data.order?.status === 'out_for_delivery') {
          setMapCenter([data.driver_location.latitude, data.driver_location.longitude]);
        }
      }
      
      // Stop polling if order is delivered or cancelled
      if (['delivered', 'picked_up', 'cancelled'].includes(data.order?.status)) {
        setIsPolling(false);
      }
      
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Tracking error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and polling
  useEffect(() => {
    fetchTracking();
    
    // Poll every 5 seconds for live updates
    if (isPolling) {
      pollIntervalRef.current = setInterval(fetchTracking, 5000);
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [orderId, isPolling]);

  // Get customer location from delivery info
  const getCustomerLocation = () => {
    if (order?.delivery_info?.latitude && order?.delivery_info?.longitude) {
      return { lat: order.delivery_info.latitude, lng: order.delivery_info.longitude };
    }
    // Default to a location near Ottawa if no coords
    return { lat: 45.4215, lng: -75.6872 };
  };

  // Get status display info
  const getStatusInfo = (status) => {
    const statusMap = {
      'pending': { label: 'Order Placed', color: 'bg-gray-500', icon: Package },
      'payment_confirmed': { label: 'Payment Confirmed', color: 'bg-green-500', icon: CheckCircle2 },
      'picking': { label: 'Being Prepared', color: 'bg-yellow-500', icon: Package },
      'picked': { label: 'Ready for Pickup', color: 'bg-blue-500', icon: CheckCircle2 },
      'assigned': { label: 'Driver Assigned', color: 'bg-purple-500', icon: Truck },
      'driver_to_store': { label: 'Driver Heading to Store', color: 'bg-orange-500', icon: Navigation },
      'driver_at_store': { label: 'Driver at Store', color: 'bg-orange-600', icon: Store },
      'out_for_delivery': { label: 'Out for Delivery', color: 'bg-blue-600', icon: Truck },
      'delivered': { label: 'Delivered', color: 'bg-green-600', icon: CheckCircle2 },
      'ready_for_pickup': { label: 'Ready for Pickup', color: 'bg-green-500', icon: Store },
      'picked_up': { label: 'Picked Up', color: 'bg-green-600', icon: CheckCircle2 },
      'cancelled': { label: 'Cancelled', color: 'bg-red-500', icon: X },
    };
    return statusMap[status] || { label: status, color: 'bg-gray-500', icon: Package };
  };

  // Calculate estimated delivery time
  const getEstimatedTime = () => {
    if (!order) return null;
    
    const statusTimes = {
      'payment_confirmed': '25-35 min',
      'picking': '20-30 min',
      'picked': '15-25 min',
      'assigned': '15-20 min',
      'driver_to_store': '10-15 min',
      'driver_at_store': '8-12 min',
      'out_for_delivery': '5-15 min',
    };
    
    return statusTimes[order.status] || null;
  };

  // Get route coordinates for polyline
  const getRouteCoords = () => {
    const coords = [];
    coords.push([STORE_LOCATION.lat, STORE_LOCATION.lng]);
    
    if (driverLocation && ['driver_to_store', 'driver_at_store', 'out_for_delivery'].includes(order?.status)) {
      coords.push([driverLocation.lat, driverLocation.lng]);
    }
    
    const customerLoc = getCustomerLocation();
    coords.push([customerLoc.lat, customerLoc.lng]);
    
    return coords;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading tracking info...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <Card className="max-w-md mx-auto p-6 text-center">
          <X className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Tracking Unavailable</h2>
          <p className="text-gray-500 mb-4">{error}</p>
          <Button onClick={onBack}>Go Back</Button>
        </Card>
      </div>
    );
  }

  const statusInfo = getStatusInfo(order?.status);
  const StatusIcon = statusInfo.icon;
  const customerLocation = getCustomerLocation();
  const estimatedTime = getEstimatedTime();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-[1000]">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BackButton onClick={onBack} />
              <div>
                <h1 className="text-lg font-bold text-gray-900">
                  Order #{order?.id?.slice(0, 8).toUpperCase()}
                </h1>
                <div className="flex items-center gap-2">
                  <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                  {isPolling && (
                    <span className="flex items-center gap-1 text-xs text-green-600">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      Live
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={fetchTracking}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="h-[45vh] sm:h-[50vh] relative">
        <MapContainer
          center={mapCenter}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <MapUpdater center={mapCenter} />
          
          {/* Route line */}
          <Polyline
            positions={getRouteCoords()}
            color="#0a1628"
            weight={4}
            opacity={0.7}
            dashArray="10, 10"
          />
          
          {/* Store marker */}
          <Marker position={[STORE_LOCATION.lat, STORE_LOCATION.lng]} icon={storeIcon}>
            <Popup>
              <div className="text-center">
                <strong>UPLANDS FOOD MARKET</strong>
                <br />
                <span className="text-sm text-gray-600">1234 Merivale Road</span>
              </div>
            </Popup>
          </Marker>
          
          {/* Customer marker */}
          <Marker position={[customerLocation.lat, customerLocation.lng]} icon={customerIcon}>
            <Popup>
              <div className="text-center">
                <strong>Delivery Address</strong>
                <br />
                <span className="text-sm text-gray-600">{order?.delivery_info?.address}</span>
              </div>
            </Popup>
          </Marker>
          
          {/* Driver marker (only when active) */}
          {driverLocation && ['driver_to_store', 'driver_at_store', 'out_for_delivery'].includes(order?.status) && (
            <Marker position={[driverLocation.lat, driverLocation.lng]} icon={driverIcon}>
              <Popup>
                <div className="text-center">
                  <strong>{order?.assigned_driver_name || 'Your Driver'}</strong>
                  <br />
                  <span className="text-sm text-gray-600">
                    {order?.status === 'out_for_delivery' ? 'On the way!' : 'Picking up your order'}
                  </span>
                </div>
              </Popup>
            </Marker>
          )}
        </MapContainer>

        {/* Estimated Time Overlay */}
        {estimatedTime && (
          <div className="absolute bottom-4 left-4 right-4 z-[1000]">
            <Card className="p-3 bg-white/95 backdrop-blur">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-500" />
                  <span className="font-medium">Estimated arrival:</span>
                </div>
                <span className="text-lg font-bold text-blue-600">{estimatedTime}</span>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Order Details */}
      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Status Progress */}
        <Card className="p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <StatusIcon className="w-5 h-5 text-blue-500" />
            Order Progress
          </h3>
          
          {/* Customer-focused status steps */}
          <div className="relative">
            {/* Progress line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
            
            <div className="space-y-0">
              {[
                { id: 'pending', label: 'Order Placed', description: 'Your order has been received', icon: Package, color: 'blue' },
                { id: 'payment_confirmed', label: 'Payment Confirmed', description: 'Payment successfully processed', icon: CreditCard, color: 'green' },
                { id: 'picking', label: 'Preparing Order', description: 'Your items are being picked', icon: Package, color: 'yellow' },
                { id: 'picked', label: 'Order Packed', description: 'Ready for driver pickup', icon: CheckCircle2, color: 'blue' },
                { id: 'assigned', label: 'Driver Assigned', description: 'A driver has accepted your order', icon: Truck, color: 'purple' },
                { id: 'out_for_delivery', label: 'Out for Delivery', description: 'Your order is on the way!', icon: Navigation, color: 'indigo' },
                { id: 'delivered', label: 'Delivered', description: 'Enjoy your order!', icon: CheckCircle2, color: 'green' },
              ].map((step, idx) => {
                const statusOrder = ['pending', 'payment_confirmed', 'picking', 'picked', 'assigned', 'driver_to_store', 'driver_at_store', 'out_for_delivery', 'delivered'];
                const currentIdx = statusOrder.indexOf(order?.status);
                const stepIdx = statusOrder.indexOf(step.id);
                const isActive = order?.status === step.id || 
                  (step.id === 'out_for_delivery' && ['driver_to_store', 'driver_at_store', 'out_for_delivery'].includes(order?.status));
                const isPast = stepIdx < currentIdx || 
                  (step.id === 'assigned' && ['driver_to_store', 'driver_at_store', 'out_for_delivery', 'delivered'].includes(order?.status));
                const Icon = step.icon;
                
                const colorClasses = {
                  blue: { bg: 'bg-blue-500', ring: 'ring-blue-200', text: 'text-blue-600' },
                  green: { bg: 'bg-green-500', ring: 'ring-green-200', text: 'text-green-600' },
                  yellow: { bg: 'bg-yellow-500', ring: 'ring-yellow-200', text: 'text-yellow-600' },
                  purple: { bg: 'bg-purple-500', ring: 'ring-purple-200', text: 'text-purple-600' },
                  indigo: { bg: 'bg-indigo-500', ring: 'ring-indigo-200', text: 'text-indigo-600' },
                };
                const colors = colorClasses[step.color];
                
                return (
                  <div key={step.id} className="relative flex items-start gap-4 pb-6 last:pb-0">
                    {/* Status dot */}
                    <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isActive 
                        ? `${colors.bg} ring-4 ${colors.ring} animate-pulse` 
                        : isPast 
                          ? 'bg-green-500' 
                          : 'bg-gray-200'
                    }`}>
                      {isPast && !isActive ? (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      ) : (
                        <Icon className={`w-4 h-4 ${isActive || isPast ? 'text-white' : 'text-gray-400'}`} />
                      )}
                    </div>
                    
                    {/* Status content */}
                    <div className="flex-1 pt-1">
                      <div className="flex items-center gap-2">
                        <p className={`font-semibold ${isActive ? colors.text : isPast ? 'text-green-600' : 'text-gray-400'}`}>
                          {step.label}
                        </p>
                        {isActive && (
                          <Badge className={`${colors.bg} text-white text-xs animate-pulse`}>
                            Current
                          </Badge>
                        )}
                        {isPast && !isActive && (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                      <p className={`text-sm ${isActive ? 'text-gray-700' : 'text-gray-400'}`}>
                        {step.description}
                      </p>
                      {isActive && statusHistory.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(statusHistory[statusHistory.length - 1]?.timestamp).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>

        {/* Driver Info (when assigned) */}
        {order?.assigned_driver_name && ['assigned', 'driver_to_store', 'driver_at_store', 'out_for_delivery'].includes(order?.status) && (
          <Card className="p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Truck className="w-5 h-5 text-orange-500" />
              Your Driver
            </h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <Truck className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="font-medium">{order.assigned_driver_name}</p>
                  <p className="text-sm text-gray-500">
                    {order.status === 'out_for_delivery' ? 'On the way to you' : 'Picking up your order'}
                  </p>
                </div>
              </div>
              {/* Call button - in production, link to real phone */}
              <Button variant="outline" size="sm">
                <Phone className="w-4 h-4 mr-1" />
                Call
              </Button>
            </div>
          </Card>
        )}

        {/* Delivery Address */}
        <Card className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-500" />
            Delivery Address
          </h3>
          <p className="text-gray-800">{order?.delivery_info?.name}</p>
          <p className="text-gray-600">{order?.delivery_info?.address}</p>
          <p className="text-gray-600">
            {order?.delivery_info?.city}, {order?.delivery_info?.province} {order?.delivery_info?.postal_code}
          </p>
          {order?.delivery_info?.instructions && (
            <p className="text-sm text-gray-500 mt-2 italic">
              Note: {order.delivery_info.instructions}
            </p>
          )}
        </Card>

        {/* Order Summary */}
        <Card className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Package className="w-5 h-5 text-green-500" />
            Order Summary
          </h3>
          <div className="space-y-2">
            {order?.items?.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span>{item.quantity}x {item.name}</span>
                <span>${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>${order?.subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Tax</span>
                <span>${order?.tax?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Delivery</span>
                <span>${order?.delivery_fee?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold mt-2">
                <span>Total</span>
                <span>${order?.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Proof of Delivery (when delivered) */}
        {order?.status === 'delivered' && order?.proof_of_delivery && (
          <Card className="p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Proof of Delivery
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {order.proof_of_delivery.photo1_url && (
                <img 
                  src={`${API_URL}${order.proof_of_delivery.photo1_url}`}
                  alt="Delivery proof 1"
                  className="w-full h-32 object-cover rounded-lg"
                />
              )}
              {order.proof_of_delivery.photo2_url && (
                <img 
                  src={`${API_URL}${order.proof_of_delivery.photo2_url}`}
                  alt="Delivery proof 2"
                  className="w-full h-32 object-cover rounded-lg"
                />
              )}
            </div>
            {order.proof_of_delivery.notes && (
              <p className="text-sm text-gray-500 mt-2">
                Note: {order.proof_of_delivery.notes}
              </p>
            )}
            <p className="text-xs text-gray-400 mt-2">
              Delivered at {new Date(order.proof_of_delivery.timestamp).toLocaleString()}
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LiveOrderTracking;
